/**
 * Card Component
 *
 * Flexible container component with optional hover effects and gradient backgrounds.
 * Provides professional shadows and smooth transitions.
 *
 * CRITICAL CONSTRAINTS:
 * - Tailwind CSS core utilities only (NO custom compilation)
 * - TypeScript strict mode (NO 'any' types)
 * - 200-300ms transition timing
 * - Professional layered shadows
 */

import React from 'react';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  hover?: boolean;
  gradient?: 'blue' | 'green' | 'purple' | 'none';
  onClick?: () => void;
}

/**
 * Card Component
 *
 * @param children - Card content
 * @param hover - Enable hover effects (scale + shadow)
 * @param gradient - Optional gradient background
 * @param className - Additional Tailwind classes
 * @param onClick - Optional click handler (makes card interactive)
 */
export function Card({
  children,
  hover = false,
  gradient = 'none',
  className = '',
  onClick,
  ...props
}: CardProps): React.JSX.Element {
  // Base classes (applied to all cards)
  const baseClasses = 'bg-white rounded-lg shadow-lg p-6 transition-all duration-200';

  // Hover classes (optional interactive state)
  const hoverClasses = hover
    ? 'hover:scale-[1.02] hover:shadow-xl cursor-pointer'
    : '';

  // Gradient background classes (optional)
  const gradientClasses: Record<string, string> = {
    blue: 'bg-gradient-to-br from-blue-50 to-blue-100',
    green: 'bg-gradient-to-br from-green-50 to-green-100',
    purple: 'bg-gradient-to-br from-purple-50 to-purple-100',
    none: 'bg-white',
  };

  // Combine classes
  const cardClasses = `${baseClasses} ${hoverClasses} ${gradientClasses[gradient]} ${className}`;

  return (
    <div
      className={cardClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
          : undefined
      }
      {...props}
    >
      {children}
    </div>
  );
}
